import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { streamAppetiteQuery, ConversationTurn } from '../../services/graphApi';
import { getSavedRecommendation } from '../../services/appetiteApi';
import { MarkdownContent, formatTime, generateId } from './chatMarkdown';
import AppetiteFeedbackPanel from './AppetiteFeedbackPanel';

// Appetite drawer — recommends the best-fit carriers for a prospect and streams
// the answer (shared SSE client, markdown renderer, and qd-* styles). The first
// recommendation is cached server-side; re-opening loads it instead of calling
// the LLM again. A Feedback toggle opens the per-carrier rating panel.

interface Message {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

interface AppetiteDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  quoteId: string;
  companyName?: string;
}

// Auto-fired the first time the guide is opened for a prospect.
const INITIAL_QUESTION = 'Which carriers are the best fit for this prospect, and why?';

const SUGGESTIONS = [
  'What are my recommended carriers?',
  'Why are these carriers recommended?',
  'Which carriers should I approach first?',
  'Which markets have appetite for this equipment?',
  'What changes if the driver has a DUI?',
  'Which standard-market carriers fit best?',
];

// ─────────────────────────────────────────────────────────────────────────────
// Message bubble + typing indicator (same markup/classes as ChatDrawer)
// ─────────────────────────────────────────────────────────────────────────────

const Bubble: React.FC<{ msg: Message }> = ({ msg }) => (
  <motion.div
    className={`qd-chat-msg qd-chat-msg--${msg.role}`}
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.18 }}
  >
    {msg.role === 'ai' ? (
      <div className="qd-chat-bubble"><MarkdownContent text={msg.content} /></div>
    ) : (
      <div className="qd-chat-bubble">{msg.content}</div>
    )}
    <span className="qd-chat-time">{formatTime(msg.timestamp)}</span>
  </motion.div>
);

const Typing: React.FC = () => (
  <motion.div className="qd-chat-msg qd-chat-msg--ai"
    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
    <div className="qd-chat-typing">
      <span className="qd-chat-dot" />
      <span className="qd-chat-dot" />
      <span className="qd-chat-dot" />
    </div>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// AppetiteDrawer
// ─────────────────────────────────────────────────────────────────────────────

const AppetiteDrawer: React.FC<AppetiteDrawerProps> = ({ isOpen, onClose, quoteId, companyName }) => {
  const [conversationId] = useState<string>(generateId);
  const [messages, setMessages]       = useState<Message[]>([]);
  const [input, setInput]             = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking]   = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const bottomRef   = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef    = useRef<AbortController | null>(null);
  const autoRef     = useRef(false);   // guards the one-time cache-load / auto recommendation

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking]);

  // ── Send message (with prior turns as context) ─────────────────────────────
  // persist=true caches this answer as the quote's saved recommendation.

  const sendMessage = useCallback((question: string, persist = false, primary = false) => {
    const text = question.trim();
    if (!text || isStreaming || !quoteId) return;

    abortRef.current?.abort();

    // Primary recommendation runs fresh (no prior context); follow-ups keep it.
    const history: ConversationTurn[] = primary ? [] : messages
      .filter((m) => m.content.trim() !== '')
      .map((m) => ({ role: m.role === 'ai' ? 'assistant' : 'user', content: m.content }));

    const userMsg: Message = { id: `u-${Date.now()}`, role: 'user', content: text, timestamp: new Date() };
    const aiId = `a-${Date.now()}`;

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsThinking(true);
    setIsStreaming(true);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    abortRef.current = streamAppetiteQuery(
      quoteId,
      text,
      conversationId,
      history,
      persist,
      (token) => {
        setIsThinking(false);
        setMessages((prev) => {
          const exists = prev.find((m) => m.id === aiId);
          if (exists) {
            return prev.map((m) => m.id === aiId ? { ...m, content: m.content + token } : m);
          }
          return [...prev, { id: aiId, role: 'ai', content: token, timestamp: new Date() }];
        });
      },
      () => {
        setIsThinking(false);
        setIsStreaming(false);
      },
      (errMsg) => {
        setIsThinking(false);
        setIsStreaming(false);
        setMessages((prev) => [...prev, {
          id: `err-${Date.now()}`, role: 'ai', content: `⚠️ ${errMsg}`, timestamp: new Date(),
        }]);
      }
    );
  }, [quoteId, conversationId, isStreaming, messages]);

  // ── On open: load the cached recommendation; generate only if none exists ──

  useEffect(() => {
    if (!isOpen) { autoRef.current = false; return; }
    if (!quoteId || messages.length > 0 || autoRef.current) return;
    autoRef.current = true;

    (async () => {
      setIsThinking(true);
      try {
        const saved = await getSavedRecommendation(quoteId);
        if (saved.found && saved.answer) {
          setIsThinking(false);
          setMessages([
            { id: 'saved-q', role: 'user', content: saved.question || INITIAL_QUESTION, timestamp: new Date() },
            { id: 'saved-a', role: 'ai', content: saved.answer, timestamp: new Date() },
          ]);
          return;
        }
      } catch {
        // Cache lookup failed — fall through to a live recommendation.
      }
      sendMessage(INITIAL_QUESTION, true, true);   // generate + cache the primary recommendation
    })();
    // Only react to open/close and quote changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, quoteId]);

  // ── Input handlers ─────────────────────────────────────────────────────────

  const onInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 100)}px`;
  };

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleClose = () => {
    abortRef.current?.abort();
    onClose();
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const isEmpty = messages.length === 0 && !isThinking;

  const panelVariants = {
    hidden:  { x: '100%', opacity: 0.6 },
    visible: { x: 0, opacity: 1, transition: { type: 'spring' as const, damping: 28, stiffness: 260 } },
    exit:    { x: '100%', opacity: 0, transition: { duration: 0.2, ease: 'easeIn' as const } },
  };
  const overlayVariants = {
    hidden:  { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } },
    exit:    { opacity: 0, transition: { duration: 0.15 } },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="qd-overlay qd-chat-overlay"
            variants={overlayVariants}
            initial="hidden" animate="visible" exit="exit"
            onClick={handleClose}
          />

          <motion.div
            className="qd-panel qd-chat-panel"
            variants={panelVariants}
            initial="hidden" animate="visible" exit="exit"
            role="dialog"
            aria-label="Appetite"
            aria-modal="true"
          >
            {/* Header */}
            <div className="qd-header">
              <div className="qd-header__left" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <div className="qd-chat-header__icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
                  </svg>
                </div>
                <div>
                  <div className="qd-chat-header__title">Appetite</div>
                  <div className="qd-chat-header__sub">{companyName || `Prospect ${quoteId}`}</div>
                </div>
              </div>

              <div className="qd-header__right" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  className={`qd-appetite-fbtn${showFeedback ? ' qd-appetite-fbtn--active' : ''}`}
                  title="Rate recommendations and record outcomes"
                  aria-pressed={showFeedback}
                  onClick={() => setShowFeedback((v) => !v)}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                  </svg>
                  Feedback
                </button>

                <button className="qd-close-btn" onClick={handleClose} aria-label="Close">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Feedback panel (per-carrier ratings + outcomes) */}
            {showFeedback ? (
              <div className="qd-body qd-chat-messages">
                <AppetiteFeedbackPanel quoteId={quoteId} />
              </div>
            ) : (
            /* Messages */
            <div className="qd-body qd-chat-messages">
              {isEmpty ? (
                <div className="qd-chat-welcome">
                  <div className="qd-chat-welcome__icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="10" />
                      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
                    </svg>
                  </div>
                  <div className="qd-chat-welcome__title">Appetite</div>
                  <div className="qd-chat-welcome__text">
                    I match this prospect against the carrier appetite graph to recommend the
                    best-fit markets, explain why, and suggest who to approach first.
                  </div>
                  <div className="qd-chat-suggestions">
                    {SUGGESTIONS.map((q) => (
                      <button key={q} className="qd-chat-suggestion" onClick={() => sendMessage(q)}>
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((msg) => <Bubble key={msg.id} msg={msg} />)}
                  {isThinking && <Typing />}
                </>
              )}
              <div ref={bottomRef} />
            </div>
            )}

            {/* Input — hidden while the feedback panel is open */}
            {!showFeedback && (
            <div className="qd-chat-input-area">
              <form onSubmit={handleSubmit}>
                <div className="qd-chat-input-row">
                  <textarea
                    ref={textareaRef}
                    className="qd-chat-textarea"
                    placeholder="Ask about carrier fit, appetite rules, who to approach first…"
                    value={input}
                    onChange={onInputChange}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    disabled={isStreaming}
                  />
                  <button
                    type="submit"
                    className="qd-chat-send"
                    disabled={!input.trim() || isStreaming}
                    aria-label="Send"
                  >
                    {isStreaming ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </svg>
                    )}
                  </button>
                </div>
              </form>
              <div className="qd-chat-hint">Enter to send · Shift+Enter for new line</div>
            </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AppetiteDrawer;
